import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/problemDetails'

import { type PresenceOverview, type PresenceStatus, sendHeartbeat, setPresenceStatus } from './api'

/**
 * Intervalo do heartbeat. O servidor considera o atendente online por 150s após o último sinal; 30s dá folga para o
 * throttling de timers de abas em segundo plano (que os navegadores espaçam para até 1 min).
 */
export const PresenceHeartbeatMs = 30_000

export function presenceQueryKey(tenantId: string) {
  return ['presence', tenantId] as const
}

function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && (error.problem.status === 403 || error.problem.status === 401)
}

/**
 * Heartbeat de presença do atendente: roda enquanto o app está aberto — INCLUSIVE em segundo plano (o atendente que
 * trocou de aba continua disponível para o cliente). Quem não pode responder conversas (403) simplesmente não entra
 * na contagem: a query para e o controle some.
 */
export function usePresence(tenantId: string | undefined) {
  return useQuery<PresenceOverview>({
    queryKey: presenceQueryKey(tenantId ?? ''),
    queryFn: () => sendHeartbeat(tenantId ?? ''),
    enabled: tenantId !== undefined,
    staleTime: PresenceHeartbeatMs,
    refetchInterval: (query) => (isForbidden(query.state.error) ? false : PresenceHeartbeatMs),
    refetchIntervalInBackground: true,
    retry: (failureCount, error) => !isForbidden(error) && failureCount < 2,
  })
}

export function isPresenceUnavailableForUser(error: unknown): boolean {
  return isForbidden(error)
}

/** Muda o status manual (Disponível/Ausente); a resposta já traz a visão atualizada da equipe. */
export function useSetPresenceStatus(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (status: PresenceStatus) => setPresenceStatus(tenantId, status),
    onSuccess: (overview) => {
      queryClient.setQueryData(presenceQueryKey(tenantId), overview)
    },
  })
}
